import { h, JSX, Fragment } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { TextField } from './TextField';
import { CloseIcon } from './Icons';
import { CardType } from '../../types/types';
import { formatPrice } from '../number-utils';

declare global {
    interface Window {
        membershipRenewalEmailTimeout?: ReturnType<typeof setTimeout>;
    }
}

export interface MembershipRenewalModalProps {
    show: boolean;
    toggleModal: () => void;
    cardType: CardType | null;
    lang: {
        title: string;
        emailLabel: string;
        emailPlaceholder: string;
        searchingLabel: string;
        monthsLabel: string;
        addToCartLabel: string;
        cancelLabel: string;
        noMemberFoundLabel: string;
        memberInfoLabel: string;
        existingCardsLabel: string;
        validUntilLabel: string;
        expiredLabel: string;
        activeLabel: string;
        renewableLabel: string;
        notRenewableLabel: string;
        newExpiryDateLabel: string;
        renewalLabel: string;
    };
    currencySymbol: string;
    searchMemberUrl: string;
    onAddToCart: (data: {
        cardTypeId: number;
        memberId: number;
        email: string;
        name: string;
        isRenewal: true;
        renewFromCardId: number;
        previousValidTo: string;
    }) => void;
}

interface MemberCard {
    id: number;
    number: string;
    type: string;
    type_id: number;
    valid_to: string;
    is_active: boolean;
}

interface MemberSearchResult {
    id: number;
    name: string;
    email: string;
    cards: MemberCard[];
}

export const MembershipRenewalModal = (props: MembershipRenewalModalProps): JSX.Element => {
    const [email, setEmail] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [existingMember, setExistingMember] = useState<MemberSearchResult | null>(null);
    const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
    const [searchAttempted, setSearchAttempted] = useState(false);

    useEffect(() => {
        if (!props.show) {
            setEmail('');
            setExistingMember(null);
            setSelectedCardId(null);
            setSearchAttempted(false);
        }
    }, [props.show]);

    const searchMember = async (emailValue: string) => {
        if (!emailValue || isSearching) {
            return;
        }

        setIsSearching(true);
        setSearchAttempted(true);
        try {
            const response = await fetch(`${props.searchMemberUrl}?q=${encodeURIComponent(emailValue)}`);
            const data = await response.json();

            if (data.data && data.data.length > 0) {
                const member = data.data[0];
                // Only set existing member if the email matches exactly
                if (member.email && member.email.toLowerCase() === emailValue.toLowerCase()) {
                    setExistingMember(member);
                    // Auto-select renewable card if only one exists
                    const renewableCards = member.cards.filter(
                        (card: MemberCard) => card.type_id === props.cardType?.id
                    );
                    if (renewableCards.length === 1) {
                        setSelectedCardId(renewableCards[0].id);
                    }
                } else {
                    setExistingMember(null);
                }
            } else {
                setExistingMember(null);
            }
        } catch (error) {
            console.error('Error searching member:', error);
            setExistingMember(null);
        } finally {
            setIsSearching(false);
        }
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    const calculateNewExpiryDate = (currentExpiry: string): string => {
        const expiryDate = new Date(currentExpiry);
        const newExpiryDate = new Date(expiryDate);
        newExpiryDate.setMonth(newExpiryDate.getMonth() + (props.cardType?.membership_duration_months || 0));
        return newExpiryDate.toLocaleDateString();
    };

    const handleAddToCart = () => {
        if (!props.cardType || !existingMember || !selectedCardId) {
            return;
        }

        const selectedCard = existingMember.cards.find((card) => card.id === selectedCardId);
        if (!selectedCard) {
            return;
        }

        props.onAddToCart({
            cardTypeId: props.cardType.id,
            memberId: existingMember.id,
            email: existingMember.email,
            name: existingMember.name,
            isRenewal: true,
            renewFromCardId: selectedCardId,
            previousValidTo: selectedCard.valid_to,
        });
        props.toggleModal();
    };

    if (!props.cardType) {
        return <div />;
    }

    const renewableCards = existingMember?.cards.filter((card) => card.type_id === props.cardType?.id) || [];

    const canRenew = existingMember && renewableCards.length > 0 && selectedCardId;

    return (
        <div
            className={
                'fixed inset-0 z-20 bg-black/60 whitespace-normal text-left flex flex-col p-4 md:p-8 lg:p-12' +
                (props.show ? '' : ' hidden')
            }
        >
            <div class="bg-white w-full max-w-[480px] m-auto rounded-lg relative overflow-auto">
                <div
                    class="absolute right-1 top-1 md:right-5 md:top-3.5 p-2 cursor-pointer rounded-full transition hover:bg-neutral-100 z-10"
                    onClick={props.toggleModal}
                >
                    <CloseIcon aria-hidden="true" />
                </div>
                <div class="flex flex-col">
                    <div class="flex flex-col p-8 grow min-h-[300px]">
                        {/* Add empty space at top to avoid X button overlap */}
                        <div class="h-8"></div>

                        {/* Membership info */}
                        <div class="p-4 bg-gray-50 rounded-lg mb-6">
                            <div class="flex justify-between items-center">
                                <span class="font-semibold">{props.cardType.name}</span>
                                <span class="font-semibold">
                                    {formatPrice(props.cardType.membership_price, props.currencySymbol)}
                                </span>
                            </div>
                            <div class="flex justify-between items-center mt-1">
                                <span class="text-sm text-gray-600">
                                    {props.cardType.membership_duration_months} {props.lang.monthsLabel}
                                </span>
                                <span class="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
                                    {props.lang.renewalLabel}
                                </span>
                            </div>
                        </div>

                        {/* Email field (required) */}
                        <div class="mb-4">
                            <TextField
                                label={
                                    props.lang.emailLabel + (isSearching ? ' ' + props.lang.searchingLabel : '') + ' *'
                                }
                                type="email"
                                value={email}
                                onChange={(value) => {
                                    setEmail(value);
                                    // Clear any existing timeout
                                    if (window.membershipRenewalEmailTimeout) {
                                        clearTimeout(window.membershipRenewalEmailTimeout);
                                    }
                                    // Set new timeout for search
                                    window.membershipRenewalEmailTimeout = setTimeout(() => {
                                        if (value) {
                                            searchMember(value);
                                        }
                                    }, 500);
                                }}
                                placeholder={props.lang.emailPlaceholder}
                                disabled={isSearching}
                                required
                            />
                        </div>

                        {/* Search results */}
                        {searchAttempted && !isSearching && (
                            <Fragment>
                                {!existingMember ? (
                                    <div class="mt-2 p-4 bg-red-50 rounded-lg">
                                        <p class="text-sm text-red-800">{props.lang.noMemberFoundLabel}</p>
                                    </div>
                                ) : (
                                    <div class="mt-2">
                                        {/* Member info */}
                                        <div class="p-4 bg-blue-50 rounded-lg mb-4">
                                            <h3 class="font-semibold text-sm mb-2">{props.lang.memberInfoLabel}</h3>
                                            <p class="text-sm">{existingMember.name}</p>
                                            <p class="text-sm text-gray-600">{existingMember.email}</p>
                                        </div>

                                        {/* Existing cards */}
                                        {existingMember.cards.length > 0 && (
                                            <div class="mb-4">
                                                <h3 class="font-semibold text-sm mb-2">
                                                    {props.lang.existingCardsLabel}
                                                </h3>
                                                <div class="space-y-2">
                                                    {existingMember.cards.map((card) => {
                                                        const isRenewable = card.type_id === props.cardType?.id;
                                                        const isSelected = card.id === selectedCardId;

                                                        return (
                                                            <div
                                                                key={card.id}
                                                                class={`p-3 rounded-lg border ${
                                                                    isRenewable
                                                                        ? isSelected
                                                                            ? 'border-blue-500 bg-blue-50'
                                                                            : 'border-gray-300 bg-white cursor-pointer hover:bg-gray-50'
                                                                        : 'border-gray-200 bg-gray-50 opacity-60'
                                                                }`}
                                                                onClick={() =>
                                                                    isRenewable && setSelectedCardId(card.id)
                                                                }
                                                            >
                                                                <div class="flex justify-between items-start">
                                                                    <div>
                                                                        <div class="font-medium">
                                                                            {card.type}: {card.number}
                                                                        </div>
                                                                        <div class="text-sm text-gray-600">
                                                                            {props.lang.validUntilLabel}:{' '}
                                                                            {formatDate(card.valid_to)}
                                                                        </div>
                                                                        <div class="text-sm">
                                                                            {card.is_active ? (
                                                                                <span class="text-green-600">
                                                                                    {props.lang.activeLabel}
                                                                                </span>
                                                                            ) : (
                                                                                <span class="text-red-600">
                                                                                    {props.lang.expiredLabel}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <div class="text-sm">
                                                                        {isRenewable ? (
                                                                            <span class="text-green-600 font-medium">
                                                                                {props.lang.renewableLabel}
                                                                            </span>
                                                                        ) : (
                                                                            <span class="text-gray-500">
                                                                                {props.lang.notRenewableLabel}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                {/* Show new expiry date for selected renewable card */}
                                                                {isRenewable && isSelected && (
                                                                    <div class="mt-3 pt-3 border-t border-blue-200">
                                                                        <div class="text-sm">
                                                                            <span class="font-medium">
                                                                                {props.lang.newExpiryDateLabel}:{' '}
                                                                            </span>
                                                                            <span class="text-blue-600">
                                                                                {calculateNewExpiryDate(card.valid_to)}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {/* No renewable cards message */}
                                        {existingMember.cards.length > 0 && renewableCards.length === 0 && (
                                            <div class="mt-2 p-4 bg-yellow-50 rounded-lg">
                                                <p class="text-sm text-yellow-800">{props.lang.notRenewableLabel}</p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </Fragment>
                        )}
                    </div>

                    {/* Footer buttons */}
                    <div class="flex shrink justify-end items-center border-t px-8 py-6">
                        <div class="flex gap-2.5">
                            <button onClick={props.toggleModal} class="btn btn-border p-4 text-base leading-[14px]">
                                {props.lang.cancelLabel}
                            </button>
                            <button
                                onClick={handleAddToCart}
                                class="btn btn-light-blue p-4 text-base leading-[14px]"
                                disabled={isSearching || !canRenew}
                            >
                                {props.lang.addToCartLabel}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
